
import { defineComponent, reactive, computed, onMounted, watch, ref, nextTick } from 'vue'
import { useForm } from '@ant-design-vue/use'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { last } from 'lodash-es'
import ClipboardJS from 'clipboard'
import { message } from 'ant-design-vue'
import { GlobalDataProps } from '../../store/index'
import { generateQRCode } from '../../helper'
import { DEFAULT_COVER_IMAGE } from '@/utils/consts'

export default defineComponent({
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['panel-close', 'publish-success', 'update:visible'],
  setup(props, { emit }) {
    const store = useStore<GlobalDataProps>()
    const route = useRoute()
    const router = useRouter()
    const activeTab = ref('channels')
    const currentWorkId = computed(() => route.params.id as string)
    const page = computed(() => store.state.editor.page)
    const channels = computed(() => store.state.editor.channels)
    const defaultChannel = computed(() => channels.value[0])

    const form = reactive({
      channelName: ''
    })
    const rules = reactive({
      channelName: [{ required: true, message: '标题不能为空', trigger: 'blur' }]
    })
    const { validate } = useForm(form, rules)
    const generateChannelURL = (id: number) =>
      `${process.env.VUE_APP_H5_URL}/pages/${page.value.id}-${page.value.uuid}?channel=${id}`
    const createChannelLoading = computed(() => store.getters.isOpLoading('createChannel'))
    const createChannel = async () => {
      const payload = {
        name: form.channelName,
        workId: parseInt(currentWorkId.value)
      }
      try {
        await validate()
        await store.dispatch('createChannel', { data: payload })
        form.channelName = ''
      } catch (e) {
        console.error(e)
      }
    }
    const deleteDisabled = computed(() => channels.value.length === 1)
    const deleteChannel = (id: number) => {
      store.dispatch('deleteChannel', { urlParams: { id } })
    }
    onMounted(() => {
      const clipboard = new ClipboardJS('.copy-button')
      clipboard.on('success', e => {
        message.success('复制成功', 1)
        e.clearSelection()
      })
      channels.value.forEach(async channel => {
        try {
          await generateQRCode(`channel-barcode-${channel.id}`, generateChannelURL(channel.id))
        } catch (e) {
          console.error(e)
        }
      })
    })
    watch(
      [() => props.visible, () => channels.value],
      async (newValues, oldValues) => {
        if (!newValues[0]) return

        await nextTick()

        if (newValues[1].length >= (oldValues[1]?.length ?? 0)) {
          // grab the last item for new channels
          const createdChannel = last(newValues[1])
          if (createdChannel) {
            await generateQRCode(`channel-barcode-${createdChannel.id}`, generateChannelURL(createdChannel.id))
          }
        }
      },
      {
        deep: true
      }
    )

    const publishAsTempLoading = computed(() => store.getters.isOpLoading('publishWorkAsTemplate'))
    const publishAsTemplate = async () => {
      await store.dispatch('publishWorkAsTemplate', { urlParams: { id: currentWorkId.value } })
      message.success('发布模板成功', 2)
      emit('publish-success')
    }
    watch(activeTab, async newVal => {
      if (newVal === 'template') {
        await nextTick()
        const { id } = defaultChannel.value
        generateQRCode(`template-barcode-${id}`, generateChannelURL(id))
      }
    })
    return {
      DEFAULT_COVER_IMAGE,
      page,
      activeTab,
      channels,
      defaultChannel,
      createChannelLoading,
      createChannel,
      form,
      rules,
      deleteDisabled,
      publishAsTemplate,
      publishAsTempLoading,
      deleteChannel,
      generateChannelURL,
      handleConfirm: () => {
        emit('update:visible', false)
        router.back()
      }
    }
  }
})
